/**
 * Yelp PII Sanitization (Story 23.5 AC #4)
 *
 * Yelp Fusion review records contain reviewer PII by default:
 *  - `user.id` (reviewer Yelp ID)
 *  - `user.name` (display name)
 *  - `user.profile_url` (link to reviewer profile)
 *  - `user.image_url` (reviewer avatar URL)
 *  - review `text` (~160 char excerpt)
 *  - business `phone` + `display_phone`
 *
 * **Round 3 Red Team Attack #3** — `sanitizeYelpPii(record)` MUST be
 * applied before any record enters `console.log`, Sentry breadcrumbs,
 * structured-log events, or queue-error payloads. The unredacted form is
 * in scope ONLY inside `fetchMetrics` → transformer → return path.
 *
 * **Round 3 Red Team Attack #4 — CSV-injection defense**.
 * `stripCsvInjectionPrefix(value)` is re-exported from CallRail's `pii.ts`
 * (single source of truth) — strips leading `=`, `+`, `@`, `-`, `\t`,
 * `\r`, `\n` characters; LOOP-strips to handle cascades like `=+@cmd`.
 *
 * Co-located fuzz tests in `pii.test.ts` assert pathological inputs
 * (PII embedded in JSON-string structured keys, URL-encoded variants).
 */
import { stripCsvInjectionPrefix as stripCsvInjectionPrefixFromCallrail } from "../callrail/pii";

/**
 * Re-export the CallRail-shipped CSV-injection prefix stripper. AC #4
 * mandates REUSE (not redeclare) — the story spec text references "Moz
 * 20.7's `moz/normalize.ts`" but the actual exported helper lives in
 * `callrail/pii.ts` (same source the Trustpilot 23.3 adapter re-exports).
 */
export const stripCsvInjectionPrefix = stripCsvInjectionPrefixFromCallrail;

/**
 * Field names this redactor scrubs from log-bound records.
 * Maintained as a Set for O(1) checks during deep traversal.
 *
 * Wire-format names (snake_case) AND camelCase forms emitted by the
 * transformers are BOTH in the set, since the redactor walks records
 * pre-transform AND post-transform.
 */
export const YELP_PII_FIELDS: ReadonlySet<string> = new Set([
  // Reviewer fields (raw + flat-camelCase forms)
  "id", // ONLY within a `user` envelope — we'd over-redact business id otherwise. See sanitizeYelpPii contract below: we redact at depth, not at top-level. To avoid the false-positive on business id we ONLY treat the `user.id` shape as PII (handled at traversal time).
  "name", // same caveat — guarded against business `name` by traversal context
  "profile_url",
  "image_url",
  "userId",
  "userName",
  "userProfileUrl",
  "userImageUrl",
  // Review body
  "text",
  "reviewExcerptText",
  // Business phone (PII-flagged per AC #12 dimensions)
  "phone",
  "display_phone",
  "displayPhone",
]);

/**
 * The set of CONTEXTUAL PII keys that ONLY count as PII when nested under
 * a `user` object (so business `id` / `name` aren't over-redacted).
 */
const USER_NESTED_PII_KEYS: ReadonlySet<string> = new Set([
  "id",
  "name",
  "profile_url",
  "image_url",
]);

/**
 * The set of UNCONDITIONAL PII keys that should be redacted wherever they
 * appear (including the flat camelCase forms that transformers emit).
 */
const UNCONDITIONAL_PII_KEYS: ReadonlySet<string> = new Set([
  "userId",
  "userName",
  "userProfileUrl",
  "userImageUrl",
  "text",
  "reviewExcerptText",
  "phone",
  "display_phone",
  "displayPhone",
]);

/**
 * Recursively redact PII fields in an arbitrary record. Returns a NEW
 * object (does not mutate input) — adapter code passes the unredacted
 * form through the data path, and only log-emission call sites pass
 * through this helper.
 *
 * Implementation walks nested objects + arrays. PII field values are
 * replaced with the literal string `"[redacted]"` so the log payload
 * shape stays intact (downstream log parsers can still grep field names
 * for forensic value).
 */
export function sanitizeYelpPii<T>(record: T): T {
  return redactDeep(record, /* underUser */ false) as T;
}

function redactDeep(value: unknown, underUser: boolean): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value !== "object") return value;
  if (Array.isArray(value)) {
    return value.map((v) => redactDeep(v, underUser));
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (UNCONDITIONAL_PII_KEYS.has(k)) {
      out[k] = "[redacted]";
      continue;
    }
    if (underUser && USER_NESTED_PII_KEYS.has(k)) {
      out[k] = "[redacted]";
      continue;
    }
    // Descend — flip the underUser flag when traversing into a `user` envelope
    // so the contextual-PII rule kicks in.
    const childUnderUser =
      k === "user" || underUser; // sticky once we enter user
    out[k] = redactDeep(v, childUnderUser);
  }
  return out;
}
